import { useState, useRef, useEffect } from "react";
import { ChevronDown, ChevronRight, Loader2, ImageIcon, Sparkles, RotateCw, Zap, Copy, Check, Pencil, Scissors, Square, Eye, Files, FolderArchive } from "lucide-react";

export default function ProductRow({
  product,
  index,
  isExpanded,
  onToggleExpand,
  onRewrite,
  onCondense,
  onDownloadImages,
  onDownloadTransparentImages,
  onDownloadWhiteImages,
  onPreviewCutouts,
  onCopy,
  onVendorEdit,
  isRewriting,
  isDownloading,
  isDownloadingTransparent,
  isDownloadingWhite,
  isCopied
}) {
  const [editingVendor, setEditingVendor] = useState(false);
  const [vendorDraft, setVendorDraft] = useState(product.vendor || "");
  const vendorInputRef = useRef(null);

  // Keep draft in sync if parent updates vendor (e.g. after initial load)
  useEffect(() => {
    if (!editingVendor) {
      setVendorDraft(product.vendor || "");
    }
  }, [product.vendor, editingVendor]);

  const startEditing = () => {
    setVendorDraft(product.vendor || "");
    setEditingVendor(true);
    setTimeout(() => vendorInputRef.current?.focus(), 0);
  };

  const commitEdit = () => {
    const trimmed = vendorDraft.trim();
    if (trimmed !== product.vendor) {
      onVendorEdit(trimmed);
    }
    setEditingVendor(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") commitEdit();
    if (e.key === "Escape") setEditingVendor(false);
  };

  const hasVariants = product.variants && product.variants.length > 0;
  const imageCount = product.images?.length || 0;
  const variantCount = product.variants?.length || 0;
  const deliveryHint =
    imageCount === 0
      ? ""
      : imageCount === 1
      ? "Downloads as 1 file"
      : imageCount <= 9
      ? `Downloads as ${imageCount} loose files`
      : "Downloads as a ZIP";
  const isZipDelivery = imageCount >= 10;

  return (
    <tr className="border-b border-[#27272A] hover:bg-[#00FFFF]/5 transition-colors" data-testid={`product-row-${index}`}>
      {/* Expand/Collapse */}
      <td className="p-4">
        {hasVariants && (
          <button
            onClick={onToggleExpand}
            className="text-zinc-400 hover:text-[#00FFFF] transition-colors"
            data-testid={`expand-btn-${index}`}
          >
            {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          </button>
        )}
      </td>

      {/* Product Image */}
      <td className="p-4">
        {product.featured_image ? (
          <img
            src={product.featured_image}
            alt={product.title}
            className="w-16 h-16 object-cover border border-[#27272A]"
            data-testid={`product-image-${index}`}
          />
        ) : (
          <div className="w-16 h-16 bg-[#111111] border border-[#27272A] flex items-center justify-center text-zinc-600 text-xs">N/A</div>
        )}
      </td>

      {/* Product Info */}
      <td className="p-4">
        <div className="text-sm text-white font-medium" data-testid={`product-title-${index}`}>
          {product.title}
        </div>

        {/* Editable vendor */}
        <div className="mt-1">
          {editingVendor ? (
            <input
              ref={vendorInputRef}
              value={vendorDraft}
              onChange={(e) => setVendorDraft(e.target.value)}
              onBlur={commitEdit}
              onKeyDown={handleKeyDown}
              className="bg-transparent border-b border-[#00FFFF] text-xs text-[#00FFFF] font-mono focus:outline-none w-full max-w-[140px] py-0.5"
              data-testid={`vendor-input-${index}`}
            />
          ) : (
            <button
              onClick={startEditing}
              className="group flex items-center gap-1.5 text-xs text-zinc-500 font-mono hover:text-zinc-300 transition-colors"
              data-testid={`vendor-edit-btn-${index}`}
              title="Click to edit brand name"
            >
              <span data-testid={`product-vendor-${index}`}>
                {product.vendor || <span className="text-zinc-700 italic">no brand</span>}
              </span>
              <Pencil className="w-2.5 h-2.5 opacity-0 group-hover:opacity-60 transition-opacity" />
            </button>
          )}
        </div>

        {hasVariants && (
          <div className="text-xs text-zinc-600 font-mono mt-1">
            {variantCount} variant{variantCount !== 1 ? "s" : ""}
          </div>
        )}
      </td>

      {/* Handle */}
      <td className="p-4">
        <div className="text-xs text-zinc-400 font-mono" data-testid={`product-handle-${index}`}>
          {product.handle}
        </div>
      </td>

      {/* Description */}
      <td className="p-4">
        {product.rewritten_description ? (
          <div className="space-y-2">
            <div className="text-xs text-zinc-300 max-h-32 overflow-y-auto pr-2 custom-scrollbar" data-testid={`rewritten-description-${index}`}>
              {product.rewritten_description}
            </div>
            {product.had_specs && (
              <div className="text-[10px] text-[#00FFFF] font-mono uppercase tracking-wider">
                ✓ Specs included
              </div>
            )}
          </div>
        ) : (
          <div className="text-xs text-zinc-500 max-w-xs line-clamp-2">
            {product.original_description || "No description"}
          </div>
        )}
      </td>

      {/* Actions */}
      <td className="p-4">
        <div className="flex flex-col gap-2">
          {imageCount > 0 && (
            <div
              className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-wider text-zinc-500"
              data-testid={`delivery-hint-${index}`}
              title={isZipDelivery
                ? "10+ images are bundled into a single ZIP file"
                : "Up to 9 images download as separate files straight to your Downloads folder"}
            >
              {isZipDelivery ? <FolderArchive className="w-3 h-3 shrink-0" /> : <Files className="w-3 h-3 shrink-0" />}
              {deliveryHint}
            </div>
          )}
          <button
            onClick={onDownloadImages}
            disabled={isDownloading || imageCount === 0}
            className="bg-[#00FFFF] text-black hover:bg-[#00CCDD] font-bold text-xs uppercase tracking-wider px-4 py-2 transition-all hover:shadow-[0_0_20px_rgba(0,255,255,0.4)] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            data-testid={`download-images-btn-${index}`}
          >
            {isDownloading ? (
              <><Loader2 className="w-3 h-3 animate-spin" />{isZipDelivery ? "ZIPPING..." : "SAVING..."}</>
            ) : (
              <><ImageIcon className="w-3 h-3" />GET IMAGES ({imageCount})</>
            )}
          </button>

          <button
            onClick={onDownloadWhiteImages}
            disabled={isDownloadingWhite || isDownloadingTransparent || isDownloading || imageCount === 0}
            className="bg-white/10 border border-white/60 text-white hover:bg-white hover:text-black font-bold text-[10px] uppercase tracking-wider px-3 py-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-1.5 text-center leading-tight"
            data-testid={`download-white-images-btn-${index}`}
            title="Product shots on clean white with a soft drop shadow; lifestyle shots stay unchanged"
          >
            {isDownloadingWhite ? (
              <><Loader2 className="w-3 h-3 animate-spin" />RENDERING...</>
            ) : (
              <><Square className="w-3 h-3 shrink-0" />GET IMAGES (WHITE BG)</>
            )}
          </button>

          <button
            onClick={onDownloadTransparentImages}
            disabled={isDownloadingTransparent || isDownloadingWhite || isDownloading || imageCount === 0}
            className="bg-[#FF00CC]/10 border border-[#FF00CC] text-[#FF8AE2] hover:bg-[#FF00CC] hover:text-black font-bold text-[10px] uppercase tracking-wider px-3 py-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-1.5 text-center leading-tight"
            data-testid={`download-transparent-images-btn-${index}`}
            title="Product shots are cut out to transparent PNGs; lifestyle shots stay unchanged"
          >
            {isDownloadingTransparent ? (
              <><Loader2 className="w-3 h-3 animate-spin" />CUTTING OUT...</>
            ) : (
              <><Scissors className="w-3 h-3 shrink-0" />GET IMAGES (TRANSPARENT BG)</>
            )}
          </button>

          <button
            onClick={onPreviewCutouts}
            disabled={imageCount === 0}
            className="bg-transparent border border-[#00FFFF]/50 text-[#00FFFF] hover:bg-[#00FFFF] hover:text-black font-bold text-[10px] uppercase tracking-wider px-3 py-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-1.5 text-center leading-tight"
            data-testid={`preview-cutouts-btn-${index}`}
            title="See how each image will look before downloading"
          >
            <Eye className="w-3 h-3 shrink-0" />PREVIEW CUT-OUTS
          </button>

          {!product.rewritten_description ? (
            <button
              onClick={onRewrite}
              disabled={isRewriting}
              className="bg-transparent border border-[#333333] text-white hover:border-[#39FF14] hover:text-[#39FF14] text-xs uppercase tracking-wider px-4 py-2 transition-all flex items-center justify-center gap-2"
              data-testid={`rewrite-btn-${index}`}
            >
              {isRewriting ? (
                <><Loader2 className="w-3 h-3 animate-spin" />REWRITING...</>
              ) : (
                <><Sparkles className="w-3 h-3" />REWRITE</>
              )}
            </button>
          ) : (
            <>
              <button
                onClick={onRewrite}
                disabled={isRewriting}
                className="bg-transparent border border-[#333333] text-white hover:border-[#FF00CC] hover:text-[#FF00CC] text-xs uppercase tracking-wider px-4 py-2 transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid={`reroll-btn-${index}`}
                title="Generate a different intro/outro variation"
              >
                {isRewriting ? (
                  <><Loader2 className="w-3 h-3 animate-spin" />REWRITING...</>
                ) : (
                  <><RotateCw className="w-3 h-3" />REWRITE</>
                )}
              </button>
              <button
                onClick={onCondense}
                disabled={isRewriting}
                className="bg-transparent border border-[#333333] text-white hover:border-[#00FFFF] hover:text-[#00FFFF] text-xs uppercase tracking-wider px-4 py-2 transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid={`condense-btn-${index}`}
                title="Generate a shorter, bullet-forward version"
              >
                {isRewriting ? (
                  <><Loader2 className="w-3 h-3 animate-spin" />WORKING...</>
                ) : (
                  <><Zap className="w-3 h-3" />CONDENSED</>
                )}
              </button>
              <button
                onClick={onCopy}
                className="bg-transparent border border-[#333333] text-white hover:border-[#39FF14] hover:text-[#39FF14] text-xs uppercase tracking-wider px-4 py-2 transition-all flex items-center justify-center gap-2"
                data-testid={`copy-btn-${index}`}
              >
                {isCopied ? (
                  <><Check className="w-3 h-3" />COPIED</>
                ) : (
                  <><Copy className="w-3 h-3" />COPY</>
                )}
              </button>
            </>
          )}
        </div>
      </td>
    </tr>
  );
}
